import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLazyGetSyncTaskStatusQuery, useLazyGetVerifyPendingOrgStatusQuery } from "state/rtkQuery/sync/syncQueries";

import { isActiveState, normalizeApiError, verifyOrgToTaskState } from "./syncFormat";

// The sync service has no "list all runs" call, so the tracker keeps the actions started from this browser (for this
// session) and checks each one by its task ID until it finishes.

// Kept per signed-in person and environment, so one person's runs never show up for another on a shared browser.
const storageKey = (email) => `ecr-ops-sync-tasks:${process.env.REACT_APP_ENVIRONMENT || "LOCAL"}:${String(email || "anonymous").toLowerCase()}`;
const MAX_TASKS = 50;
const TICK_MS = 2000;
// Poll often at first, then back off. A task that is still PENDING after this long may never have reached a worker (or
// its result expired), so automatic checking stops and the person can press Refresh.
const STALE_PENDING_MS = 30 * 60 * 1000;

const readStored = (key) => {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(key) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const writeStored = (key, tasks) => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(tasks.slice(0, MAX_TASKS)));
  } catch (e) {
    // Storage can be unavailable (private window): the tracker still works for this page view.
  }
};

export const pollDelay = (startedAt, now = Date.now()) => {
  const age = now - startedAt;
  if (age < 2 * 60 * 1000) return 5000;
  if (age < 10 * 60 * 1000) return 10000;
  return 30000;
};

const SyncTasksContext = createContext(null);

export const SyncTasksProvider = ({ children }) => {
  const email = useSelector((state) => state.auth?.userAccount?.username);
  const key = storageKey(email);
  const [tasks, setTasks] = useState(() => readStored(key));
  const tasksRef = useRef(tasks);
  const inFlight = useRef(new Set());
  const [fetchStatus] = useLazyGetSyncTaskStatusQuery();
  const [fetchOrgStatus] = useLazyGetVerifyPendingOrgStatusQuery();

  const commit = useCallback((updater) => {
    setTasks((current) => {
      const next = updater(current);
      tasksRef.current = next;
      writeStored(key, next);
      return next;
    });
  }, [key]);

  const patchTask = useCallback(
    (id, patch) => commit((current) => current.map((task) => (task.id === id ? { ...task, ...patch } : task))),
    [commit]
  );

  // Starts tracking a task the service has just accepted.
  const trackTask = useCallback(
    (task) => {
      const startedAt = task.startedAt ?? Date.now();
      commit((current) => [
        {
          kind: "generic",
          state: "PENDING",
          result: null,
          error: null,
          lookupFailed: false,
          stalePending: false,
          ...task,
          startedAt,
          nextPollAt: startedAt + 3000,
        },
        ...current.filter((existing) => existing.id !== task.id),
      ].slice(0, MAX_TASKS));
    },
    [commit]
  );

  const pollTask = useCallback(
    async (task) => {
      if (inFlight.current.has(task.id)) return;
      inFlight.current.add(task.id);
      const now = Date.now();
      try {
        if (task.kind === "verify-org") {
          const response = await fetchOrgStatus({ orgId: task.orgId }, false);
          if (response.isError) throw response.error;
          const data = response.data || {};
          // never_run right after accepting only means "not picked up yet" (it also covers an expired result).
          patchTask(task.id, {
            state: verifyOrgToTaskState(data.status),
            result: data.status === "complete" || data.status === "error" ? { success: data.success, message: data.message, stats: data.stats, error: data.error } : task.result,
            error: data.status === "error" ? data.error || data.message || "Failed" : null,
            note: data.message || null,
            lookupFailed: false,
            lastCheckedAt: now,
            nextPollAt: now + pollDelay(task.startedAt, now),
          });
        } else {
          const response = await fetchStatus({ taskId: task.id }, false);
          if (response.isError) throw response.error;
          const data = response.data || {};
          if (data.status === "ERROR") {
            // The status lookup itself failed: not proof that the task failed.
            patchTask(task.id, { lookupFailed: true, lookupMessage: data.error || "Couldn't read the status", lastCheckedAt: now, nextPollAt: now + pollDelay(task.startedAt, now) });
          } else {
            patchTask(task.id, {
              // A stored (database) record reports its own state: "submitted" is still waiting for a worker.
              state: data.status || task.state,
              result: data.result ?? data.record?.result_summary ?? null,
              error: data.error ?? data.record?.error ?? null,
              statusSource: data.status_source || null,
              unknownOrExpired: false,
              lookupFailed: false,
              lastCheckedAt: now,
              nextPollAt: now + pollDelay(task.startedAt, now),
            });
          }
        }
      } catch (error) {
        if (error?.status === 404 && task.kind !== "verify-org") {
          // With resolve_unknown the service says 404 when it has no live status AND no retained record: the id is
          // unknown or expired. That is not a failure of the task, and there is nothing left to wait for.
          patchTask(task.id, { unknownOrExpired: true, lookupFailed: false, stalePending: true, lastCheckedAt: now });
        } else {
          patchTask(task.id, { lookupFailed: true, lookupMessage: normalizeApiError(error).message, lastCheckedAt: now, nextPollAt: now + pollDelay(task.startedAt, now) });
        }
      } finally {
        inFlight.current.delete(task.id);
      }
    },
    [fetchOrgStatus, fetchStatus, patchTask]
  );

  // Checks again right now (the Refresh button), including a task whose automatic checking stopped.
  const refreshTask = useCallback(
    (id) => {
      const task = tasksRef.current.find((item) => item.id === id);
      if (task) {
        patchTask(id, { stalePending: false, unknownOrExpired: false });
        return pollTask(task);
      }
      return undefined;
    },
    [patchTask, pollTask]
  );

  useEffect(() => {
    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      const now = Date.now();
      tasksRef.current.forEach((task) => {
        if (!isActiveState(task.state) || task.stalePending) return;
        if (task.state === "PENDING" && now - task.startedAt > STALE_PENDING_MS) {
          patchTask(task.id, { stalePending: true });
          return;
        }
        if (now >= (task.nextPollAt ?? 0)) pollTask(task);
      });
    };
    const timer = setInterval(tick, TICK_MS);
    return () => clearInterval(timer);
  }, [patchTask, pollTask]);

  const clearFinished = useCallback(() => commit((current) => current.filter((task) => isActiveState(task.state))), [commit]);

  const value = useMemo(() => ({ tasks, trackTask, refreshTask, clearFinished }), [tasks, trackTask, refreshTask, clearFinished]);
  return <SyncTasksContext.Provider value={value}>{children}</SyncTasksContext.Provider>;
};

export const useSyncTasks = () => {
  const context = useContext(SyncTasksContext);
  if (!context) throw new Error("useSyncTasks must be used inside SyncTasksProvider");
  return context;
};
